/**
 * PRP-ROOF-7 W3 (D9/D10): the vendor-measured roof attached to an estimate.
 *
 * Contract: GET / POST (multipart `file`) / DELETE /api/v1/imports/{parse_hash}/roof
 * (app/api/routes/roof_attach.py). A refused file comes back 200 with `attached: false`
 * and the reason; nothing was stored. DELETE drops the pointer only. Same auth/fetch path
 * as importMetadataApi.ts; failures arrive as `ApiError`.
 */
import { api } from './api';

export type RoofSourceKind = 'vendor_xml' | 'vendor_esx';

export interface RoofLineView {
  code: string;
  calc: string;
  qty: number;
  unit: string;
  description?: string;
}

export interface RoofView {
  vendor: string;
  source_kind: RoofSourceKind;
  filename_label: string;
  attached_at: string;
  faces: number;
  sf: number;
  sq: number;
  p: number;
  r: number;
  hip: number;
  val: number;
  stepflash?: number;
  status: string;
  lines: RoofLineView[];
}

export interface RoofAttached {
  attached: true;
  roof: RoofView;
  export_blockers: string[];
}

export interface RoofNotAttached {
  attached: false;
  roof?: null;
  export_blockers?: string[];
}

export interface RoofRefused {
  attached: false;
  reason: string;
  code: string;
}

export type RoofState = RoofAttached | RoofNotAttached;
export type RoofAttachResult = RoofAttached | RoofRefused;

const path = (parseHash: string) => `/imports/${encodeURIComponent(parseHash)}/roof`;

export const getRoof = async (parseHash: string): Promise<RoofState> => api.get(path(parseHash));

export const attachRoof = async (parseHash: string, file: File): Promise<RoofAttachResult> => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(path(parseHash), formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const removeRoof = async (parseHash: string): Promise<{ attached: false; removed: boolean }> =>
  api.delete(path(parseHash));

export const isRefused = (r: RoofAttachResult): r is RoofRefused => !r.attached && 'reason' in r;

export const SOURCE_KIND_LABEL: Record<RoofSourceKind, string> = {
  vendor_xml: 'vendor XML',
  vendor_esx: 'roof ESX',
};
